import { fetchPlant } from './api_plants';
import { fetchCustomPlant } from './api_customPlants';
import { ws_unassignPot, ws_updatePot } from './api_wsFunctions.js';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Handle fetch responses safely.
 * - Parse JSON only if content-type is JSON
 * - On error, expose status and message
 */
async function handleResponse(response) {
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    let body = null;
    try {
        body = isJson ? await response.json() : await response.text();
    } catch (_) {
        body = null;
    }

    if (!response.ok) {
        const message =
            (body && typeof body === 'object' && body.message) ||
            (typeof body === 'string' && body.trim()) ||
            `HTTP ${response.status} ${response.statusText}`;

        return { error: true, status: response.status, message, data: body };
    }

    return { error: false, status: response.status, data: body };
}

/**
 * Generic fetch wrapper
 */
async function fetchData(url, options = {}) {
    try {
        const response = await fetch(url, options);
        return await handleResponse(response);
    } catch (error) {
        console.error('Fetch error:', error);
        return { error: true, message: error.message };
    }
}

/**
 * Fetch a single pot by ID
 */
export async function fetchPot(potID, token) {
    if (!potID) {
        return { error: true, message: 'potID is required' };
    }
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    };
    return fetchData(`${API_URL}/pots/${encodeURIComponent(potID)}`, options);
}

/**
 * Fetch a pot ID by SN
 */
export async function fetchPotIDBySN(potSN, token) {
    if (!potSN) {
        return { error: true, message: 'potSN is required' };
    }
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    };
    return fetchData(`${API_URL}/pots/bySN/${encodeURIComponent(potSN)}`, options);
}

/**
 * Create an empty pot
 */
export async function createEmptyPot(mac, name, hubID, token) {
    if (!mac || !name || !hubID) {
        return { error: true, message: 'mac, name, and hubID are required' };
    }
    const url = new URL(`${API_URL}/pots/addEmpty`);
    url.searchParams.append('mac', mac);
    url.searchParams.append('name', name);
    url.searchParams.append('hub_id', hubID);

    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    };

    return fetchData(url.toString(), options);
}

/**
 * Update an existing pot
 */
export async function updatePot(potID, pot, ignore_plant_averages, token, potSN) {
    if (!potID || !pot) {
        return { error: true, message: 'Both potID and pot data are required' };
    }
    const options = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(pot)
    };
    try {
        ws_updatePot(potSN);
    } catch (_) {}
    return fetchData(
        `${API_URL}/pots/${encodeURIComponent(potID)}?ignore_plant_averages=${ignore_plant_averages}`,
        options
    );
}

/**
 * Unassign a pot from a hub
 */
export async function unassignPot(hubSN, potID, potSN, token) {
    const potResponse = await fetchPot(potID, token);
    if (potResponse.error) {
        console.error('Error fetching pot details:', potResponse.message);
        return potResponse;
    }

    // Change null to 0 or another sentinel if your API requires it
    const updatedPot = { ...potResponse.data, hub_id: null };

    const updateResponse = await updatePot(
        potResponse.data.id,
        updatedPot,
        false,
        token,
        potSN
    );

    if (updateResponse.error) {
        console.error('Failed to update pot:', updateResponse.message, updateResponse.data);
        return updateResponse;
    }

    try {
        ws_unassignPot(hubSN, potSN);
    } catch (e) {
        console.warn('WS unassign notification failed:', e);
    }

    return { error: false, message: 'Pot successfully unassigned' };
}

/**
 * Update irrigation plan
 */
export async function update_pot_irrigation_plan(potID, irrigationPlanTimes, irrigationPlanHour, token, potSN) {
    if (potID == null || irrigationPlanTimes == null || irrigationPlanHour == null) {
        return { error: true, message: 'All potID, irrigationPlanTimes and irrigationPlanHour are required' };
    }

    const options = {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
            irrigationPlanTimes,
            irrigationPlanHour
        })
    };

    try {
        ws_updatePot(potSN);
        const res = await fetchData(`${API_URL}/pots/${encodeURIComponent(potID)}/updateIrrigation`, options);
        if (res.error) return res;
        return { error: false, data: res.data };
    } catch (error) {
        return { error: true, message: error.message };
    }
}

/**
 * Delete a plant from pot
 */
export async function deletePlantFromPot(potID, plantID, token) {
    if (!potID || !plantID) {
        return { error: true, message: 'Both potID and plantID are required' };
    }
    try {
        const pot = await fetchPot(potID, token);
        if (pot.error) return pot;

        const plantsArray = pot.data.plant_ids ? pot.data.plant_ids.split(',') : [];
        const index = plantsArray.findIndex((p) => p.trim() === plantID.toString());
        if (index === -1) {
            return { error: true, message: 'Plant ID not found in pot device' };
        }
        plantsArray.splice(index, 1);
        const updatedPot = { ...pot.data, plant_ids: plantsArray.join(',') };
        return await updatePot(potID, updatedPot, false, token);
    } catch (error) {
        return { error: true, message: error.message };
    }
}

/**
 * Delete a custom plant from pot
 */
export async function deleteCustomPlantFromPot(potID, customPlantID, token) {
    if (!potID || !customPlantID) {
        return { error: true, message: 'Both potID and customPlantID are required' };
    }
    try {
        const pot = await fetchPot(potID, token);
        if (pot.error) return pot;

        const customPlantsArray = pot.data.custom_plant_ids ? pot.data.custom_plant_ids.split(',') : [];
        const index = customPlantsArray.findIndex((p) => p.trim() === customPlantID.toString());
        if (index === -1) {
            return { error: true, message: 'Custom plant ID not found in pot device' };
        }
        customPlantsArray.splice(index, 1);
        const updatedPot = { ...pot.data, custom_plant_ids: customPlantsArray.join(',') };
        return await updatePot(potID, updatedPot, false, token);
    } catch (error) {
        return { error: true, message: error.message };
    }
}

/**
 * Fetch plants for a pot
 */
export async function fetchPlantsForPot(potID, token) {
    if (!potID) {
        return { error: true, message: 'potID is required' };
    }
    try {
        const pot = await fetchPot(potID, token);
        if (pot.error) return pot;

        const plantIds = pot.data.plant_ids ? pot.data.plant_ids.split(',') : [];
        const customPlantIds = pot.data.custom_plant_ids ? pot.data.custom_plant_ids.split(',') : [];

        const plantsPromises = plantIds.map((id) => fetchPlant(id.trim()));
        const customPlantsPromises = customPlantIds.map((id) => fetchCustomPlant(id.trim()));

        const plants = await Promise.all(plantsPromises);
        const customPlants = await Promise.all(customPlantsPromises);

        return { error: false, plants, customPlants };
    } catch (error) {
        return { error: true, message: error.message };
    }
}

/**
 * Fetch last measure
 */
export async function fetchLastMeasureForPot(potID, token) {
    if (!potID) {
        return { error: true, message: 'potID is required' };
    }
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    };
    return fetchData(`${API_URL}/pots/${encodeURIComponent(potID)}/lastMeasure`, options);
}

/**
 * Fetch all measures
 */
export async function fetchMeasuresForPot(potID, token) {
    if (!potID) {
        return { error: true, message: 'potID is required' };
    }
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    };
    return fetchData(`${API_URL}/pots/${encodeURIComponent(potID)}/measures`, options);
}

/**
 * Fetch irrigations
 */
export async function fetchIrrigationsForPot(potID, token) {
    if (!potID) {
        return { error: true, message: 'potID is required' };
    }
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    };
    return fetchData(`${API_URL}/pots/${encodeURIComponent(potID)}/irrigations`, options);
}

/**
 * Fetch measures by date range
 */
export async function fetchMeasuresForPotByDate(potID, startDate, endDate, token) {
    if (!potID) {
        return { error: true, message: 'potID is required' };
    }
    if (!startDate || !endDate) {
        return { error: true, message: 'Start date and end date must be provided' };
    }

    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    };

    const formattedStartDate = new Date(startDate).toISOString();
    const formattedEndDate = new Date(endDate).toISOString();

    return fetchData(
        `${API_URL}/pots/${encodeURIComponent(potID)}/measuresbyDate/?start_date=${encodeURIComponent(formattedStartDate)}&end_date=${encodeURIComponent(formattedEndDate)}`,
        options
    );
}
